import { useState } from 'react';
import { Dialog, Button, TextField, CheckBox, Tooltip } from '@workspace/design-system/src/components';
import type { PaymentChannel } from '@uniquegood/realworld-admin-interface';

export interface PlanCancelEventDialogProps {
  planName?: string;
  paymentChannel: PaymentChannel | null | undefined;
  options: PlanCancelDialogOption[];
  planEnded: boolean;
  onClose: () => void;
  onConfirm: (payload: { reason: string; optionId: string }) => Promise<void>;
}

export interface PlanCancelDialogOption {
  id: string;
  label?: string;
  disabled?: boolean;
  data?: Record<string, unknown>;
}

export function PlanCancelEventDialog({
  planName = '',
  paymentChannel,
  options,
  planEnded,
  onClose,
  onConfirm,
}: PlanCancelEventDialogProps) {
  const [cancelReason, setCancelReason] = useState('');
  const [isCheckpointChecked, setIsCheckpointChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const isInvoice = paymentChannel === 'Invoice';
  const isTossPG = paymentChannel === 'TossPG';
  const isNoPayment = paymentChannel === null || paymentChannel === undefined;

  // 체크박스가 필요한 케이스: 직접결제(Invoice)만
  const requiresCheckbox = isInvoice;

  // 제목 결정
  const getTitle = () => {
    if (planEnded) {
      return `${planName} 요금제를 사용 취소하시겠습니까?`;
    }
    return '연장 내역을 취소하시겠습니까?';
  };

  // 안내 문구 렌더링
  const renderDescription = () => {
    if (isTossPG) {
      if (planEnded) {
        // 토스 PG, 1개
        return (
          <>
            <p>취소 사유 입력 후 진행시,</p>
            <ul className="list-disc ml-5">
              <li>토스에 관련 결제 취소가 요청되며</li>
              <li>
                <span className="font-bold">요청 처리 후</span> <span className="font-bold">채널이 무료 요금제로 전환</span>됩니다.
              </li>
            </ul>
          </>
        );
      } else {
        // 토스 PG, 2개 이상
        return (
          <>
            <p>취소 사유 입력 후 진행시,</p>
            <ul className="list-disc ml-5">
              <li>토스에 관련 결제 취소가 요청되며</li>
              <li>
                <span className="font-bold">요청 처리 후</span> <span className="font-bold">요금제 연장이 취소</span>됩니다.
              </li>
            </ul>
          </>
        );
      }
    } else if (isInvoice) {
      if (planEnded) {
        // 직접 결제, 1개
        return (
          <>
            <p>
              취소 사유 입력 후 진행시, 채널이 <span className="font-bold">무료 요금제로 전환</span>됩니다.
            </p>
            <p>
              <span className="font-bold">반드시 세금계산서 취소 및 환불이 완료된 후</span> 연장 취소를 진행해 주세요.
            </p>
          </>
        );
      } else {
        // 직접 결제, 2개 이상
        return (
          <>
            <p>
              취소 사유 입력 후 진행시, <span className="font-bold">요금제 연장이 취소</span>됩니다.
            </p>
            <p>
              <span className="font-bold">반드시 세금계산서 취소 및 환불이 완료된 후</span> 연장 취소를 진행해 주세요.
            </p>
          </>
        );
      }
    } else if (isNoPayment) {
      if (planEnded) {
        // 결제 없이, 1개
        return (
          <p>
            취소 사유 입력 후 진행시, 채널이 <span className="font-bold">무료 요금제로 전환</span>됩니다.
          </p>
        );
      } else {
        // 결제 없이, 2개 이상
        return (
          <>
            <p>
              취소 사유 입력 후 진행시, <span className="font-bold">요금제 연장이 취소</span>됩니다.
            </p>
          </>
        );
      }
    }

    return null;
  };

  const handleConfirm = async () => {
    if (!cancelReason.trim()) return;
    if (!options.length) return;
    if (requiresCheckbox && !isCheckpointChecked) return;

    setIsLoading(true);
    try {
      await onConfirm({ reason: cancelReason.trim(), optionId: options[0].id });
    } finally {
      setIsLoading(false);
    }
  };

  const hasValidReason = cancelReason.trim().length > 0;
  const isConfirmDisabled = !hasValidReason || options.length === 0 || (requiresCheckbox && !isCheckpointChecked) || isLoading;

  return (
    <Dialog>
      <Dialog.Content>
        <Dialog.Header title={getTitle()} type="destructive" showIcon />
        <Dialog.Body>
          <div className="flex flex-col gap-4">
            {/* 이벤트 리스트 */}
            {options.length > 0 && (
              <div className="flex flex-col gap-2 rounded-lg px-3 py-2 border border-[#F0F0F0]">
                {options.map((option, index) => {
                  const isTarget = index === 0;
                  const textColor = isTarget ? '#101010' : '#BFBFBF';
                  return (
                    <div key={option.id} className="flex items-center gap-3">
                      <div className="flex flex-wrap gap-1 text-sm leading-5" style={{ color: textColor }}>
                        <span className="text-lg leading-5">•</span>
                        <Tooltip content={option.id}>
                          <span>{option.label ?? '-'}</span>
                        </Tooltip>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* 안내 문구 */}
            <div className="flex flex-col gap-2 text-sm leading-5 text-[#101010]">
              {renderDescription()}
              <p>&nbsp;</p>
              <p className="text-[#D15445]">※ 취소 후에는 복구할 수 없습니다.</p>
            </div>

            {/* 체크박스 - Invoice(직접결제)만 표시 */}
            {requiresCheckbox && (
              <button
                type="button"
                onClick={() => setIsCheckpointChecked((prev) => !prev)}
                className="flex items-center gap-2 text-left"
              >
                <CheckBox checked={isCheckpointChecked} onChange={() => setIsCheckpointChecked((prev) => !prev)} />
                <span className="text-sm leading-5 text-[#101010]">세금계산서 취소 및 환불을 완료했습니다.</span>
              </button>
            )}

            {/* 취소 사유 입력 */}
            <div className="flex flex-col gap-2">
              <TextField
                value={cancelReason}
                onChange={(value) => setCancelReason(value)}
                placeholder="취소 사유를 입력해 주세요"
              />
            </div>
          </div>
        </Dialog.Body>
        <Dialog.Footer>
          <Button type="button" styleType="basic" styleSize="M-40" onClick={onClose}>
            닫기
          </Button>
          <Button
            type="button"
            styleType="destructive"
            styleSize="M-40"
            disabled={isConfirmDisabled}
            onClick={handleConfirm}
          >
            {isLoading ? '취소 처리 중...' : '취소하기'}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  );
}
